import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const Section = ({ title, children }) => (
  <section className="border-t border-slate-200 pt-7 first:border-t-0 first:pt-0">
    <h2 className="font-[Playfair_Display] text-2xl font-semibold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-7 text-slate-700">{children}</div>
  </section>
);

export const GoogleApiPrivacyPage = () => {
  useEffect(() => {
    document.title = "Google-API-Datenschutzhinweise | Business Studio Suite";
  }, []);

  return (
    <main className="min-h-screen bg-[#f7f4ee] px-4 py-8 text-slate-900" data-testid="google-api-privacy-page">
      <div className="mx-auto max-w-4xl">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#9c742e]">Business Studio Suite</p>
            <h1 className="mt-1 font-[Playfair_Display] text-3xl font-semibold">Ergänzende Datenschutzhinweise: Google OAuth und Gmail API</h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
              Diese Hinweise erläutern ergänzend zur <Link className="underline" to="/datenschutz">allgemeinen Datenschutzerklärung</Link>,
              wie die freiwillige Google- bzw. Gmail-Verbindung im CRM Studio verarbeitet wird.
            </p>
          </div>
          <Link
            to="/google-info"
            className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            data-testid="google-api-privacy-back"
          >
            Zur Google-Information
          </Link>
        </header>

        <article className="space-y-7 rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <Section title="1. Freiwillige Verbindung und Berechtigungen">
            <p>
              Berechtigte Nutzerinnen und Nutzer einer Organisation können im CRM Studio freiwillig ein eigenes Google-Konto verbinden.
              Die Verbindung wird erst eingerichtet, nachdem die jeweilige Person den Vorgang bewusst startet und die von Google angezeigte
              Berechtigung erteilt. Es wird kein Google-Konto automatisch verbunden.
            </p>
            <p>
              Abhängig von den aktivierten Funktionen verwendet die Business Studio Suite die Google-Berechtigungen <code>gmail.readonly</code>
              {' '}zum Lesen von E-Mails im Posteingang und <code>gmail.send</code> zum Versenden von E-Mails im Namen des verbundenen Kontos.
            </p>
          </Section>

          <Section title="2. Welche Google-Nutzerdaten verarbeitet werden">
            <p>
              Für die Einrichtung der Verbindung wird die E-Mail-Adresse des verbundenen Google-Kontos über das Google-Profil ermittelt.
              Beim aktivierten E-Mail-Eingang verarbeitet die Suite die für die CRM-Funktion benötigten Daten der E-Mail-Vorgänge:
              Absenderadresse, Betreff, Klartext-Inhalt, Nachrichten- und Thread-Kennungen sowie weitere technische Metadaten,
              die für die Zuordnung eines E-Mail-Vorgangs erforderlich sind.
            </p>
            <p>
              Dateianhänge werden durch die aktuelle Gmail-Integration nicht als Dateiinhalte abgerufen oder verarbeitet.
            </p>
          </Section>

          <Section title="3. Zwecke der Verarbeitung">
            <p>
              Die Google-Nutzerdaten werden ausschließlich für die in der Business Studio Suite sichtbaren und von der Organisation aktivierten
              CRM-Funktionen verwendet: organisationsbezogener E-Mail-Empfang, zentrale CRM-Inbox, Zuordnung zu Leads oder Kunden,
              Darstellung in der bestehenden Kommunikationschronik sowie Versand ausdrücklich freigegebener E-Mails.
            </p>
            <p>
              Eine eindeutige Zuordnung kann automatisch erfolgen. Bei unklarer Zuordnung zeigt die Suite Vorschläge zur manuellen Auswahl;
              sie ordnet eine solche E-Mail nicht stillschweigend einem falschen Kontakt zu. E-Mails werden nur nach einer ausdrücklichen
              Versandhandlung durch eine berechtigte Nutzerin oder einen berechtigten Nutzer versendet.
            </p>
          </Section>

          <Section title="4. Speicherung, Sicherheit und Mandantentrennung">
            <p>
              Die für die Google-Verbindung notwendigen OAuth-Zugangsdaten (Access Token und Refresh Token) werden serverseitig verschlüsselt
              gespeichert. E-Mail-Inhalte und die für den jeweiligen Kommunikationsvorgang benötigten Metadaten werden innerhalb der
              zugehörigen Organisation in der CRM-Kommunikationschronik bzw. der zentralen CRM-Inbox gespeichert. Der Zugriff ist
              organisationsbezogen abgegrenzt.
            </p>
            <p>
              Für diese CRM-Daten gelten die allgemeinen Regelungen der Datenschutzerklärung, insbesondere zu Speicherdauer,
              Löschung und gesetzlichen Aufbewahrungspflichten. Eine eigenständige, feste technische Speicherfrist nur für Gmail-Daten
              ist derzeit nicht hinterlegt.
            </p>
          </Section>

          <Section title="5. Weitergabe und KI-Antwortentwürfe">
            <p>
              Google-Nutzerdaten werden nicht verkauft und nicht für Werbung, Profilbildung, Bonitätsentscheidungen oder nicht
              nutzerbezogene KI- oder Machine-Learning-Modelle verwendet.
            </p>
            <p>
              Eine Übermittlung von Inhalten eines E-Mail-Vorgangs an einen externen KI-Anbieter erfolgt nur, wenn die betreffende
              Organisation die Funktion für KI-Antwortentwürfe eingerichtet hat und eine berechtigte Nutzerin oder ein berechtigter Nutzer
              diesen Entwurf ausdrücklich anfordert. Dabei werden die für den angeforderten Entwurf benötigten Inhalte des betreffenden
              E-Mail-Vorgangs an den von der Organisation konfigurierten KI-Anbieter übermittelt. Ohne ausdrückliche Anforderung wird
              kein KI-Antwortentwurf erzeugt und keine entsprechende Übermittlung vorgenommen.
            </p>
            <p>
              Im Übrigen findet keine Weitergabe von Google-Nutzerdaten an Dritte statt, soweit sie nicht zur Bereitstellung der ausdrücklich
              angeforderten, in der Oberfläche sichtbaren Funktion, zur Sicherheit, zur Erfüllung gesetzlicher Pflichten oder mit ausdrücklicher
              Einwilligung erforderlich ist.
            </p>
          </Section>

          <Section title="6. Widerruf und Löschung">
            <p>
              Die Google-Verbindung kann jederzeit unter <strong>CRM Studio → E-Mail → Gemeinsames Organisationspostfach → Trennen</strong>
              aufgehoben werden. Dadurch werden die für diese Organisationsverbindung gespeicherten OAuth-Zugangsdaten entfernt und der
              weitere Zugriff der Business Studio Suite auf das Google-Konto endet.
            </p>
            <p>
              Der Google-Zugriff kann zusätzlich in den Einstellungen des jeweiligen Google-Kontos widerrufen werden. Bereits in der
              CRM-Kommunikationschronik gespeicherte E-Mail-Kopien werden durch das Trennen der Verbindung nicht automatisch gelöscht.
              Ein Löschersuchen zu gespeicherten CRM-Daten kann an die in der allgemeinen Datenschutzerklärung genannte verantwortliche
              Stelle gerichtet werden; gesetzliche Aufbewahrungspflichten bleiben unberührt.
            </p>
          </Section>

          <Section title="7. Google API Services User Data Policy und Limited Use">
            <p>
              Die Nutzung von Informationen, die über Google APIs bezogen werden, erfolgt entsprechend der{' '}
              <a className="underline" href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">
                Google API Services User Data Policy
              </a>, einschließlich der Anforderungen zur eingeschränkten Nutzung (Limited Use).
              Google-Nutzerdaten werden ausschließlich zur Bereitstellung oder Verbesserung der für die Nutzerin oder den Nutzer sichtbaren
              und ausdrücklich angeforderten Funktionen der Business Studio Suite verwendet.
            </p>
          </Section>
        </article>

        <p className="mx-auto mt-6 max-w-4xl text-xs leading-5 text-slate-500">
          Stand: 29.09.2026 · Diese Ergänzung erläutert die technische Google- und Gmail-Verarbeitung der Business Studio Suite.
          Die allgemeinen Betroffenenrechte und Kontaktangaben stehen in der allgemeinen Datenschutzerklärung.
        </p>
      </div>
    </main>
  );
};

export default GoogleApiPrivacyPage;
